import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, IconButton, CircularProgress } from "@mui/material";
import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  MODAL_IMAGE_EDIT_IMAGE_ALT,
  MODAL_IMAGE_EDIT_IMAGE_NOT_FOUND,
  MODAL_IMAGE_EDIT_START_CREATING,
  MODAL_IMAGE_EDIT_LAYERS_ANALYZING,
  CANVAS_TOOL_ERASER_APPLY,
} from "../../../../i18n/keysTranslations";
import ImageActions from "../single/ImageActions";
import ScannerOverlay from "./ScannerOverlay";
import ObjectEraserOverlay from "./ObjectEraserOverlay";
import { buildEraseLayerObjectRequest } from "./eraseLayerObject";
import Button from "../../../Buttons/Button";
import {
  LAYER_TYPE,
  LAYERS_NORMALIZED_MAX,
  getLayerType,
  isLayerVisible,
  normalizedToPixels,
  pixelsToNormalized,
} from "./layerGeometry";
import { fitTextInBox } from "../../../../features/creativeTemplates/instance/fitTextInBox";
import {
  isItalicTextStyle,
  isUnderlineTextStyle,
  resolveTextShadow,
} from "../../../../features/creativeTemplates/utils/textStyle";
import { resolveTextOrientation } from "../../../../features/creativeTemplates/utils/textOrientation";
import FittedTextPreview from "../../../../features/creativeTemplates/utils/FittedTextPreview";
import { useInlineReplacementMetrics } from "../../../../features/creativeTemplates/utils/useInlineReplacementMetrics";
import { urlsFromInlineReplacementAssetRefs } from "../../../../features/creativeTemplates/utils/textInlineReplacements";
import ZoomControls from "../../../../features/creativeTemplates/editor/ZoomControls";
import AlignmentGuidesToggle from "../../../../features/creativeTemplates/editor/AlignmentGuidesToggle";
import {
  CANVAS_VIEWPORT_PADDING,
  canvasZoomBoardSx,
  getCanvasZoomLayout,
  layoutBoxToVisual,
  MAX_ZOOM,
  useCanvasZoom,
} from "../../../../features/creativeTemplates/editor/useCanvasZoom";
import { useFreezeViewportScroll } from "../../../../features/creativeTemplates/editor/useFreezeViewportScroll";
import SelectionChromeOverlay, {
  CANVAS_SELECTION_HANDLE_CLASS,
} from "../../../../features/creativeTemplates/editor/SelectionChromeOverlay";
import AlignmentGuidesOverlay from "../../../../features/creativeTemplates/editor/AlignmentGuidesOverlay";
import RotationAngleOverlay, {
  syncRotationAngleGuide,
} from "../../../../features/creativeTemplates/editor/RotationAngleOverlay";
import {
  applyDragAlignmentSnap,
  boxesOutsideDraggedBranch,
} from "../../../../features/creativeTemplates/editor/alignmentGuides";
import { resizePixelBoxFromHandle } from "../../../../features/creativeTemplates/editor/resizePixelBoxFromHandle";
import {
  canCropImageLayer,
  cropPixelBoxFromHandle,
  isCropHandleGesture,
} from "../../../../features/creativeTemplates/editor/layerCrop";
import { CroppedLayerImage } from "../../../../features/creativeTemplates/editor/CroppedLayerImage";
import { useShiftHeld } from "../../../../features/creativeTemplates/editor/useShiftHeld";
import {
  getLiveMarqueeSelection,
  isAdditiveLayerSelect,
} from "../../../../features/creativeTemplates/editor/canvasLayerSelection";
import {
  idsIntersectingMarquee,
  MARQUEE_MIN_SIZE_PX,
  marqueeOverlaySx,
  normalizeMarqueeRect,
} from "../../../../features/creativeTemplates/editor/canvasMarquee";
import {
  GROUP_SELECTION_ID,
  axisAlignedBounds,
  groupSelectionChrome,
  scaleBoxesToBounds,
  translateBoxes,
} from "../../../../features/creativeTemplates/editor/canvasGroupTransform";
import {
  canvasArtboardSx,
  canvasWorkspaceSx,
  canvasZoomHideScrollbarsSx,
  canvasZoomViewportSx,
} from "../../../../features/creativeTemplates/editor/canvasWorkspace";
import { pickTopmostCanvasHit } from "../../../../utils/pickCanvasHit";
import {
  ROTATE_HANDLE_CORNER,
  cssRotateTransform,
  pointInRotatedBox,
  rotationDegreesFromDrag,
  unrotatePoint,
  boxCenter,
} from "../../../../features/creativeTemplates/editor/layerRotation";

const MIN_LAYER_SIZE_PX = 8;
const SELECTION_COLOR = "#111";

const LayerEraseProgressOverlay = ({ box }) => (
  <Box
    sx={{
      position: "absolute",
      left: box.x,
      top: box.y,
      width: box.width,
      height: box.height,
      pointerEvents: "none",
      zIndex: 8,
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <ScannerOverlay visible />
    <CircularProgress size={28} sx={{ color: "#18181B", zIndex: 1 }} />
  </Box>
);

const isResizeHandleEvent = (event) =>
  Boolean(
    event.target instanceof Element &&
      event.target.closest(`.${CANVAS_SELECTION_HANDLE_CLASS}`)
  );

/**
 * Converts a viewport pointer into the artboard's layout pixels.
 * Uses the visual rect so CSS zoom / parent transforms stay 1:1 with the cursor.
 */
const clientToCanvasPixels = (event, canvas) => {
  const rect = canvas.getBoundingClientRect();
  if (!rect.width || !rect.height) {
    return { x: 0, y: 0 };
  }
  return {
    x: ((event.clientX - rect.left) / rect.width) * canvas.offsetWidth,
    y: ((event.clientY - rect.top) / rect.height) * canvas.offsetHeight,
  };
};

const constrainBoxMinSize = (box) => ({
  ...box,
  width: Math.max(box.width, MIN_LAYER_SIZE_PX),
  height: Math.max(box.height, MIN_LAYER_SIZE_PX),
});

const hitTestEditableLayer = ({
  layers,
  point,
  displayWidth,
  displayHeight,
  preferText = false,
}) => {
  const hits = [...layers]
    .filter((layer) => isLayerVisible(layer))
    .sort(
      (left, right) => (Number(left.zIndex) || 0) - (Number(right.zIndex) || 0)
    )
    .map((layer, paintIndex) => {
      const box = normalizedToPixels({
        x: layer.x,
        y: layer.y,
        width: layer.width,
        height: layer.height,
        rotation: layer.rotation,
        displayWidth,
        displayHeight,
      });
      return {
        layer,
        box,
        isBackground: Boolean(layer.isBackground),
        paintIndex,
        zIndex: layer.zIndex,
        area: box.width * box.height,
      };
    })
    .filter(({ box }) => pointInRotatedBox({ point, box }));

  if (preferText) {
    const textHit = pickTopmostCanvasHit({
      hits: hits.filter((hit) => getLayerType(hit.layer) === LAYER_TYPE.TEXT),
    });
    if (textHit) return textHit.layer;
  }

  return pickTopmostCanvasHit({ hits })?.layer || null;
};

const canSelectTextInLayer = (layer) =>
  Boolean(layer) && getLayerType(layer) === LAYER_TYPE.TEXT;

const LayerContent = ({
  layer,
  alt,
  isEditingText,
  onTextCommit,
  isSelectingText = false,
  selectedPhrase = "",
  onSelectionChange,
  scale,
  canvasWidth,
  canvasHeight,
}) => {
  const type = getLayerType(layer);
  const inlineImages = useMemo(
    () =>
      type === LAYER_TYPE.TEXT
        ? urlsFromInlineReplacementAssetRefs(layer.style?.inlineReplacements)
        : {},
    [layer.style?.inlineReplacements, type]
  );
  const replacementMetrics = useInlineReplacementMetrics({
    urls: inlineImages,
  });
  switch (type) {
    case LAYER_TYPE.IMAGE:
      return layer.url ? (
        <CroppedLayerImage
          src={layer.url}
          alt={alt}
          crop={layer.crop}
          objectFit="fill"
        />
      ) : null;
    case LAYER_TYPE.TEXT: {
      const style = layer.style || {};
      const orientation = resolveTextOrientation(style.orientation);
      const fontFamily = style.fontAssetRef || "sans-serif";
      const fontWeight =
        Number(style.fontWeight) > 0 ? Number(style.fontWeight) : 400;
      const requestedFontSize = Number(style.fontSize);
      const hasRequestedFontSize =
        Number.isFinite(requestedFontSize) && requestedFontSize > 0;
      const italic = isItalicTextStyle(style.textStyle);
      const underline = isUnderlineTextStyle(style.textStyle);
      const boxWidth =
        ((Number(layer.width) || 0) / LAYERS_NORMALIZED_MAX) *
        (canvasWidth || 0);
      const boxHeight =
        ((Number(layer.height) || 0) / LAYERS_NORMALIZED_MAX) *
        (canvasHeight || 0);
      const letterSpacing = Number(style.letterSpacing);
      const layout = fitTextInBox({
        text: layer.text,
        boxWidth,
        boxHeight,
        fontFamily,
        requestedFontSize: hasRequestedFontSize ? requestedFontSize : undefined,
        lineHeightMult: style.lineHeight,
        letterSpacing: Number.isFinite(letterSpacing)
          ? letterSpacing
          : undefined,
        orientation,
        italic,
        underline,
        fontWeight,
        highlights: style.highlights,
        strokeWidth: Number(style.strokeWidth) || 0,
        inlineReplacements: style.inlineReplacements,
        replacementMetrics,
      });
      return (
        <FittedTextPreview
          text={layer.text}
          layout={layout}
          fallbackFontSize={hasRequestedFontSize ? requestedFontSize : 12}
          scale={scale}
          color={style.color || "#fff"}
          fontFamily={fontFamily}
          fontWeight={fontWeight}
          align={style.align || "left"}
          orientation={orientation}
          textStyle={style.textStyle}
          letterSpacing={Number.isFinite(letterSpacing) ? letterSpacing : 0}
          strokeWidth={Number(style.strokeWidth) || 0}
          strokeColor={style.strokeColor}
          shadow={resolveTextShadow(style)}
          backgroundPadding={Number(style.backgroundPadding) || 0}
          backgroundColor={style.backgroundColor}
          backgroundRadius={style.backgroundRadius}
          highlights={style.highlights}
          inlineReplacements={style.inlineReplacements}
          replacementMetrics={replacementMetrics}
          inlineImages={inlineImages}
          isEditing={isEditingText}
          onCommit={(nextText) => onTextCommit(layer.id, nextText)}
          isSelectingText={isSelectingText}
          selectedPhrase={selectedPhrase}
          onSelectionChange={onSelectionChange}
        />
      );
    }
    default: {
      const exhaustive = type;
      throw new Error(`Unhandled layer type: ${exhaustive}`);
    }
  }
};

const MovableLayer = memo(function MovableLayer({
  layer,
  pixelBox,
  isSelected,
  isEditingText,
  isSelectingText = false,
  selectedPhrase = "",
  onTextSelectionChange,
  alt,
  scale,
  canvasWidth,
  canvasHeight,
  onTextCommit,
}) {
  return (
    <Box
      sx={{
        position: "absolute",
        left: pixelBox.x,
        top: pixelBox.y,
        width: Math.max(pixelBox.width, MIN_LAYER_SIZE_PX),
        height: Math.max(pixelBox.height, MIN_LAYER_SIZE_PX),
        zIndex: Number(layer.zIndex) || 0,
        border: isSelected
          ? "1.5px solid transparent"
          : "1px solid transparent",
        pointerEvents: "auto",
        overflow: "visible",
        boxSizing: "border-box",
        transform: cssRotateTransform(pixelBox.rotation ?? layer.rotation),
        transformOrigin: "center center",
      }}
      onPointerDown={(event) => {
        if (isEditingText) {
          event.stopPropagation();
        }
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          // Text clips itself, wide enough to keep a highlight pill whole where
          // it hugs past the layer box; anything else stays bound to the box.
          overflow: canSelectTextInLayer(layer) ? "visible" : "hidden",
          pointerEvents: isEditingText || isSelectingText ? "auto" : "none",
          userSelect: isSelectingText ? "text" : undefined,
          cursor: isSelectingText ? "text" : undefined,
        }}
      >
        <LayerContent
          layer={layer}
          alt={alt}
          isEditingText={isEditingText}
          onTextCommit={onTextCommit}
          isSelectingText={isSelectingText}
          selectedPhrase={selectedPhrase}
          onSelectionChange={(state) =>
            onTextSelectionChange?.(layer.id, state)
          }
          scale={scale}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
        />
      </Box>
    </Box>
  );
});

const ImageArtboardLayers = memo(function ImageArtboardLayers({
  layers,
  selectedSet,
  editingTextID,
  selectedTextNodeID,
  selectedTextPhrase,
  onTextCommit,
  onTextSelectionChange,
  altFallback,
  scale,
  canvasWidth,
  canvasHeight,
  dragBoxes,
  dragLayerId,
  dragBox,
  displayWidth,
  displayHeight,
}) {
  return [...layers]
    .sort(
      (left, right) => (Number(left.zIndex) || 0) - (Number(right.zIndex) || 0)
    )
    .map((layer) => {
      if (!isLayerVisible(layer)) return null;
      const pixelBox = dragBoxes?.[layer.id]
        ? dragBoxes[layer.id]
        : dragLayerId === layer.id
          ? dragBox
          : layer.isBackground
            ? {
                x: 0,
                y: 0,
                width: displayWidth,
                height: displayHeight,
              }
            : normalizedToPixels({
                x: layer.x,
                y: layer.y,
                width: layer.width,
                height: layer.height,
                rotation: layer.rotation,
                displayWidth,
                displayHeight,
              });
      const isSelected = selectedSet.includes(layer.id);

      if (layer.isBackground) {
        return (
          <Box
            key={layer.id}
            component="img"
            src={layer.url}
            alt={layer.name || altFallback}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "fill",
              zIndex: Number(layer.zIndex) || 0,
              pointerEvents: "none",
            }}
          />
        );
      }

      const displayLayer = {
        ...layer,
        crop: Object.prototype.hasOwnProperty.call(pixelBox || {}, "crop")
          ? pixelBox.crop
          : layer.crop,
      };
      return (
        <MovableLayer
          key={layer.id}
          layer={displayLayer}
          pixelBox={pixelBox}
          isSelected={isSelected}
          isEditingText={editingTextID === layer.id}
          isSelectingText={
            editingTextID === layer.id && canSelectTextInLayer(layer)
          }
          selectedPhrase={
            selectedTextNodeID === layer.id ? selectedTextPhrase : ""
          }
          onTextSelectionChange={onTextSelectionChange}
          alt={layer.name || altFallback}
          scale={scale}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
          onTextCommit={onTextCommit}
        />
      );
    });
});

const LayerCanvas = ({
  imageUrl,
  initialImageUrl,
  layers = [],
  canvasWidth,
  canvasHeight,
  isAnalyzing = false,
  selectedLayerId,
  selectedLayerIds = [],
  editingTextID,
  onTextSelectionChange,
  selectedTextNodeID = null,
  selectedTextPhrase = "",
  onSelectLayer,
  onSelectLayers,
  onLayerBoxChange,
  onLayerBoxesChange,
  onStartTextEdit = () => {},
  onTextCommit = () => {},
  isSidebarOpen,
  onToggleSidebar,
  onClose,
  onCallbackSave = () => {},
  showSaveAlert = true,
  saveDisabled = true,
  showCloseButton = true,
  showSaveActions = true,
  toolbar = null,
  isObjectEraserActive = false,
  objectEraserBrushFraction = 0.1,
  erasingLayerIds = {},
  onEraseObject = () => {},
  onHasObjectEraserMaskChange = () => {},
  onRequestExitObjectEraser = () => {},
  hasObjectEraserMask = false,
}) => {
  const { t } = useTranslationApp();
  const viewportRef = useRef(null);
  const artboardRef = useRef(null);
  const eraserOverlayRef = useRef(null);
  const maxZoomRef = useRef(MAX_ZOOM);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [activeDrag, setActiveDrag] = useState(null);
  const activeDragRef = useRef(null);
  const [marquee, setMarquee] = useState(null);
  const [alignmentGuidesEnabled, setAlignmentGuidesEnabled] = useState(true);
  const marqueeRef = useRef(null);
  const shiftHeld = useShiftHeld();
  const zoomLockRef = useRef(null);
  const alignmentGuidesRef = useRef(null);
  const rotationAngleRef = useRef(null);
  const snapContextRef = useRef({
    getOtherBoxes: () => [],
    artboardWidth: 0,
    artboardHeight: 0,
    minSize: MIN_LAYER_SIZE_PX,
  });
  activeDragRef.current = activeDrag;
  zoomLockRef.current = activeDrag || marquee || null;
  maxZoomRef.current = MAX_ZOOM;
  const hasLayers = layers.length > 0;
  const { zoom, viewportSize, onZoomIn, onZoomOut, onFit } = useCanvasZoom({
    viewportRef,
    boardRef: artboardRef,
    resetKey: `${imageUrl}:${canvasWidth}x${canvasHeight}`,
    lockRef: zoomLockRef,
    maxZoomRef,
  });
  useFreezeViewportScroll({
    viewportRef,
    frozen: Boolean(activeDrag) || Boolean(marquee),
  });
  const boardWidth = canvasWidth || naturalSize.width;
  const boardHeight = canvasHeight || naturalSize.height;
  const aspect = boardWidth && boardHeight ? boardWidth / boardHeight : 1;
  const { fitWidth, fitHeight } = useMemo(() => {
    const pad = CANVAS_VIEWPORT_PADDING * 2;
    const availableWidth = Math.max(1, viewportSize.width - pad);
    const availableHeight = Math.max(1, viewportSize.height - pad);
    if (availableWidth / availableHeight > aspect) {
      return {
        fitHeight: availableHeight,
        fitWidth: availableHeight * aspect,
      };
    }
    return {
      fitWidth: availableWidth,
      fitHeight: availableWidth / aspect,
    };
  }, [aspect, viewportSize.height, viewportSize.width]);
  const zoomLayout = getCanvasZoomLayout({
    viewportWidth: viewportSize.width,
    viewportHeight: viewportSize.height,
    fitWidth,
    fitHeight,
    zoom,
  });
  const { padX, padY, contentWidth, contentHeight } = zoomLayout;
  const displaySize = {
    width: viewportSize.width && boardWidth ? fitWidth : 0,
    height: viewportSize.height && boardHeight ? fitHeight : 0,
  };
  snapContextRef.current = {
    getOtherBoxes: (excludeId) =>
      boxesOutsideDraggedBranch({
        boxes: layers.filter(isLayerVisible),
        excludeId,
      }).map((layer) =>
        normalizedToPixels({
          x: layer.x,
          y: layer.y,
          width: layer.width,
          height: layer.height,
          rotation: layer.rotation,
          displayWidth: displaySize.width,
          displayHeight: displaySize.height,
        })
      ),
    artboardWidth: displaySize.width,
    artboardHeight: displaySize.height,
    minSize: MIN_LAYER_SIZE_PX,
    guideScale: zoom,
  };
  const scale =
    canvasWidth && displaySize.width ? displaySize.width / canvasWidth : 1;
  const eraserLayer = useMemo(() => {
    if (!isObjectEraserActive || !selectedLayerId) return null;
    const layer = layers.find((item) => item.id === selectedLayerId);
    if (!layer || !isLayerVisible(layer)) return null;
    if (getLayerType(layer) !== LAYER_TYPE.IMAGE) return null;
    return layer;
  }, [isObjectEraserActive, layers, selectedLayerId]);
  const eraserPixelBox = useMemo(() => {
    if (!eraserLayer || !displaySize.width || !displaySize.height) return null;
    if (eraserLayer.isBackground) {
      return {
        x: 0,
        y: 0,
        width: displaySize.width,
        height: displaySize.height,
      };
    }
    return normalizedToPixels({
      x: eraserLayer.x,
      y: eraserLayer.y,
      width: eraserLayer.width,
      height: eraserLayer.height,
      rotation: eraserLayer.rotation,
      displayWidth: displaySize.width,
      displayHeight: displaySize.height,
    });
  }, [displaySize.height, displaySize.width, eraserLayer]);

  useEffect(() => {
    setNaturalSize({ width: 0, height: 0 });
    if (!imageUrl || (canvasWidth && canvasHeight)) {
      return undefined;
    }
    let cancelled = false;
    const image = new Image();
    image.onload = () => {
      if (cancelled) return;
      setNaturalSize({
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    };
    image.src = imageUrl;
    return () => {
      cancelled = true;
    };
  }, [canvasHeight, canvasWidth, imageUrl]);

  useEffect(() => {
    if (!isObjectEraserActive) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onRequestExitObjectEraser();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isObjectEraserActive, onRequestExitObjectEraser]);

  const committedSelectedIDs = useMemo(() => {
    if (selectedLayerIds.length) return selectedLayerIds;
    return selectedLayerId ? [selectedLayerId] : [];
  }, [selectedLayerId, selectedLayerIds]);

  const layerToPixelBox = (layer) =>
    normalizedToPixels({
      x: layer.x,
      y: layer.y,
      width: layer.width,
      height: layer.height,
      rotation: layer.rotation,
      displayWidth: displaySize.width,
      displayHeight: displaySize.height,
    });

  const isMarqueeSelecting =
    Boolean(marquee) &&
    ((Number(marquee.w) || 0) >= MARQUEE_MIN_SIZE_PX ||
      (Number(marquee.h) || 0) >= MARQUEE_MIN_SIZE_PX);
  const marqueeHitIds = isMarqueeSelecting
    ? idsIntersectingMarquee({
        boxes: layers,
        marquee,
        toBounds: (layer) => layerToPixelBox(layer),
        skip: (layer) => layer.isBackground || !isLayerVisible(layer),
      })
    : [];
  const liveSelection = getLiveMarqueeSelection({
    committedIDs: committedSelectedIDs,
    committedPrimary: selectedLayerId,
    hitIds: marqueeHitIds,
    additive: Boolean(marquee?.additive),
    active: isMarqueeSelecting,
  });
  const selectedSet = liveSelection.ids;
  const livePrimaryID = liveSelection.primary;

  const commitDragBoxes = (drag) => {
    if (!displaySize.width || !displaySize.height) return;
    const boxesMap =
      drag.boxes ||
      (drag.box && drag.layerId ? { [drag.layerId]: drag.box } : {});
    const updates = Object.entries(boxesMap).map(([id, box]) => ({
      id,
      box: {
        ...pixelsToNormalized({
          ...box,
          displayWidth: displaySize.width,
          displayHeight: displaySize.height,
        }),
        ...(Object.prototype.hasOwnProperty.call(box, "crop")
          ? { crop: box.crop }
          : {}),
      },
    }));
    if (!updates.length) return;
    if (typeof onLayerBoxesChange === "function") {
      onLayerBoxesChange(updates);
      return;
    }
    updates.forEach((item) => onLayerBoxChange(item.id, item.box));
  };
  const commitDragBoxesRef = useRef(commitDragBoxes);
  commitDragBoxesRef.current = commitDragBoxes;

  const onArtboardPointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (isAnalyzing) return;
    if (isObjectEraserActive) return;
    if (editingTextID) return;
    if (isResizeHandleEvent(event)) return;
    const canvas = artboardRef.current;
    if (!canvas || !displaySize.width || !displaySize.height) return;

    const point = clientToCanvasPixels(event, canvas);
    const hitLayer = hitTestEditableLayer({
      layers,
      point,
      displayWidth: displaySize.width,
      displayHeight: displaySize.height,
    });
    if (!hitLayer) return;

    event.preventDefault();
    onSelectLayer(hitLayer.id, event, { preserveMulti: true });
    if (isAdditiveLayerSelect(event)) return;
    if (hitLayer.isBackground || hitLayer.locked) return;

    event.currentTarget.setPointerCapture(event.pointerId);

    const selectedMoving = layers.filter(
      (item) =>
        selectedSet.includes(item.id) &&
        isLayerVisible(item) &&
        !item.isBackground &&
        !item.locked
    );
    const moving =
      selectedSet.includes(hitLayer.id) && selectedMoving.length > 1
        ? selectedMoving
        : [hitLayer];
    const originBoxes = moving.map((item) => ({
      id: item.id,
      ...layerToPixelBox(item),
    }));
    const boxes = Object.fromEntries(originBoxes.map((box) => [box.id, box]));
    const box = boxes[hitLayer.id];
    const nextDrag = {
      layerId: hitLayer.id,
      ids: moving.map((item) => item.id),
      mode: "move",
      box,
      boxes,
      originBoxes,
      startPoint: point,
      grabOffset: {
        x: point.x - box.x,
        y: point.y - box.y,
      },
    };
    activeDragRef.current = nextDrag;
    setActiveDrag(nextDrag);
  };

  const onArtboardPointerMove = (event) => {
    if (!event.currentTarget.hasPointerCapture?.(event.pointerId)) return;
    const canvas = artboardRef.current;
    if (!canvas) return;
    const current = activeDragRef.current;
    if (
      !current ||
      current.mode === "resize" ||
      current.mode === "rotate" ||
      current.mode === "crop"
    )
      return;
    const point = clientToCanvasPixels(event, canvas);
    if ((current.ids || []).length > 1) {
      const dx = point.x - current.startPoint.x;
      const dy = point.y - current.startPoint.y;
      const moved = translateBoxes({
        boxes: current.originBoxes,
        dx,
        dy,
      });
      const boxes = Object.fromEntries(moved.map((box) => [box.id, box]));
      const next = {
        ...current,
        boxes,
        box: boxes[current.layerId],
      };
      activeDragRef.current = next;
      setActiveDrag(next);
      alignmentGuidesRef.current?.clear();
      return;
    }
    const proposed = constrainBoxMinSize({
      ...current.box,
      x: point.x - current.grabOffset.x,
      y: point.y - current.grabOffset.y,
    });
    const snapped = applyDragAlignmentSnap({
      box: proposed,
      mode: "move",
      event,
      excludeId: current.layerId,
      prevSnap: current.snap,
      snapContext: snapContextRef.current,
      guidesRef: alignmentGuidesRef,
      enabled: alignmentGuidesEnabled,
    });
    const next = {
      ...current,
      box: snapped.box,
      boxes: { [current.layerId]: snapped.box },
      snap: snapped.snap,
    };
    activeDragRef.current = next;
    setActiveDrag(next);
  };

  const onArtboardPointerUp = (event) => {
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const current = activeDragRef.current;
    if (
      !current ||
      current.mode === "resize" ||
      current.mode === "rotate" ||
      current.mode === "crop"
    )
      return;
    activeDragRef.current = null;
    setActiveDrag(null);
    alignmentGuidesRef.current?.clear();
    commitDragBoxes(current);
  };

  const onPointerDownWorkspace = (event) => {
    if (isAnalyzing || isObjectEraserActive || editingTextID) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (typeof onSelectLayers !== "function") return;
    if (artboardRef.current?.contains(event.target)) return;
    if (isResizeHandleEvent(event)) return;
    const canvas = artboardRef.current;
    if (!canvas || !displaySize.width || !displaySize.height) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const point = clientToCanvasPixels(event, canvas);
    const next = {
      x: point.x,
      y: point.y,
      w: 0,
      h: 0,
      originX: point.x,
      originY: point.y,
      additive: isAdditiveLayerSelect(event),
    };
    marqueeRef.current = next;
    setMarquee(next);
  };

  const onPointerMoveWorkspace = (event) => {
    const current = marqueeRef.current;
    if (!current) return;
    const canvas = artboardRef.current;
    if (!canvas) return;
    const point = clientToCanvasPixels(event, canvas);
    const next = {
      originX: current.originX,
      originY: current.originY,
      additive: isAdditiveLayerSelect(event),
      ...normalizeMarqueeRect({
        originX: current.originX,
        originY: current.originY,
        x: point.x,
        y: point.y,
      }),
    };
    marqueeRef.current = next;
    setMarquee(next);
  };

  const onPointerUpWorkspace = (event) => {
    const current = marqueeRef.current;
    marqueeRef.current = null;
    setMarquee(null);
    if (!current || typeof onSelectLayers !== "function") return;
    const ids = idsIntersectingMarquee({
      boxes: layers,
      marquee: current,
      toBounds: (layer) => layerToPixelBox(layer),
      skip: (layer) => layer.isBackground || !isLayerVisible(layer),
    });
    onSelectLayers(ids, event);
  };

  const onOverlayHandlePointerDown = ({ event, id, corner }) => {
    event.preventDefault();
    event.stopPropagation();
    if (isObjectEraserActive || editingTextID) return;
    const canvas = artboardRef.current;
    if (!canvas || !displaySize.width) return;
    const point = clientToCanvasPixels(event, canvas);
    if (id === GROUP_SELECTION_ID) {
      const moving = layers.filter(
        (item) =>
          selectedSet.includes(item.id) &&
          isLayerVisible(item) &&
          !item.isBackground &&
          !item.locked
      );
      if (moving.length < 2) return;
      const originBoxes = moving.map((item) => ({
        id: item.id,
        ...layerToPixelBox(item),
      }));
      const boxes = Object.fromEntries(originBoxes.map((box) => [box.id, box]));
      const nextDrag = {
        layerId: moving[0].id,
        ids: moving.map((item) => item.id),
        mode: "resize",
        corner,
        startPoint: point,
        originBoxes,
        originBounds: axisAlignedBounds(originBoxes),
        boxes,
        box: boxes[moving[0].id],
      };
      activeDragRef.current = nextDrag;
      setActiveDrag(nextDrag);
      return;
    }
    const layer = layers.find((item) => item.id === id);
    if (!layer || layer.isBackground || layer.locked) return;
    const box = layerToPixelBox(layer);
    const isCrop =
      corner !== ROTATE_HANDLE_CORNER &&
      isCropHandleGesture({
        event,
        shiftHeld,
        layer: {
          type: getLayerType(layer),
          isBackground: layer.isBackground,
        },
        selectedCount: 1,
      });
    const nextDrag = {
      layerId: layer.id,
      ids: [layer.id],
      mode:
        corner === ROTATE_HANDLE_CORNER ? "rotate" : isCrop ? "crop" : "resize",
      corner,
      startPoint: point,
      originBox: box,
      originBoxes: [{ id: layer.id, ...box }],
      originRotation: Number(box.rotation) || 0,
      originCrop: layer.crop,
      box,
      boxes: { [layer.id]: box },
    };
    activeDragRef.current = nextDrag;
    setActiveDrag(nextDrag);
    if (nextDrag.mode === "rotate") {
      syncRotationAngleGuide(rotationAngleRef, {
        degrees: nextDrag.originRotation,
        event,
      });
    }
  };

  useEffect(() => {
    const onMove = (event) => {
      const current = activeDragRef.current;
      if (
        !current ||
        (current.mode !== "resize" &&
          current.mode !== "rotate" &&
          current.mode !== "crop")
      ) {
        return;
      }
      const canvas = artboardRef.current;
      if (!canvas) return;
      const point = clientToCanvasPixels(event, canvas);
      if ((current.ids || []).length > 1 && current.mode === "resize") {
        const nextBounds = constrainBoxMinSize(
          resizePixelBoxFromHandle({
            origin: current.originBounds,
            corner: current.corner,
            startPoint: current.startPoint,
            point,
            minSize: MIN_LAYER_SIZE_PX,
          })
        );
        const scaled = scaleBoxesToBounds({
          boxes: current.originBoxes,
          originBounds: current.originBounds,
          nextBounds,
          minSize: MIN_LAYER_SIZE_PX,
        });
        const boxes = Object.fromEntries(scaled.map((box) => [box.id, box]));
        const next = {
          ...current,
          boxes,
          box: boxes[current.layerId],
        };
        activeDragRef.current = next;
        setActiveDrag(next);
        alignmentGuidesRef.current?.clear();
        return;
      }
      const originRotation = Number(current.originRotation) || 0;
      let nextBox = current.box;
      if (current.mode === "crop") {
        const originBox = current.originBox;
        const center = boxCenter(originBox);
        const cropped = cropPixelBoxFromHandle({
          originFrame: originBox,
          originCrop: current.originCrop,
          corner: current.corner,
          startPoint: originRotation
            ? unrotatePoint({
                point: current.startPoint,
                center,
                degrees: originRotation,
              })
            : current.startPoint,
          point: originRotation
            ? unrotatePoint({
                point,
                center,
                degrees: originRotation,
              })
            : point,
          minSize: MIN_LAYER_SIZE_PX,
        });
        nextBox = {
          x: cropped.x,
          y: cropped.y,
          width: cropped.width,
          height: cropped.height,
          rotation: originRotation,
          crop: cropped.crop,
        };
        const next = {
          ...current,
          box: nextBox,
          boxes: { [current.layerId]: nextBox },
        };
        activeDragRef.current = next;
        setActiveDrag(next);
        alignmentGuidesRef.current?.clear();
        return;
      }
      if (current.mode === "rotate") {
        nextBox = {
          ...current.originBox,
          rotation: rotationDegreesFromDrag({
            center: boxCenter(current.originBox),
            startPoint: current.startPoint,
            currentPoint: point,
            originRotation,
          }),
        };
        syncRotationAngleGuide(rotationAngleRef, {
          degrees: nextBox.rotation,
          event,
        });
      } else if (!originRotation) {
        nextBox = constrainBoxMinSize(
          resizePixelBoxFromHandle({
            origin: current.originBox,
            corner: current.corner,
            startPoint: current.startPoint,
            point,
            minSize: MIN_LAYER_SIZE_PX,
          })
        );
        nextBox.rotation = originRotation;
      } else {
        const center = boxCenter(current.originBox);
        nextBox = constrainBoxMinSize(
          resizePixelBoxFromHandle({
            origin: current.originBox,
            corner: current.corner,
            startPoint: unrotatePoint({
              point: current.startPoint,
              center,
              degrees: originRotation,
            }),
            point: unrotatePoint({
              point,
              center,
              degrees: originRotation,
            }),
            minSize: MIN_LAYER_SIZE_PX,
          })
        );
        nextBox.rotation = originRotation;
      }
      const snapped = applyDragAlignmentSnap({
        box: nextBox,
        mode: current.mode,
        corner: current.corner,
        event,
        excludeId: current.layerId,
        prevSnap: current.snap,
        snapContext: snapContextRef.current,
        guidesRef: alignmentGuidesRef,
        enabled: alignmentGuidesEnabled,
      });
      const next = {
        ...current,
        box: snapped.box,
        boxes: { [current.layerId]: snapped.box },
        snap: snapped.snap,
      };
      activeDragRef.current = next;
      setActiveDrag(next);
    };
    const onUp = () => {
      const current = activeDragRef.current;
      if (
        !current ||
        (current.mode !== "resize" &&
          current.mode !== "rotate" &&
          current.mode !== "crop")
      ) {
        return;
      }
      activeDragRef.current = null;
      setActiveDrag(null);
      alignmentGuidesRef.current?.clear();
      rotationAngleRef.current?.clear();
      commitDragBoxesRef.current(current);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    // eslint-disable-next-line
  }, []);

  const livePixelBoxForLayer = (layer) => {
    if (!layer) return null;
    if (activeDrag?.boxes?.[layer.id]) return activeDrag.boxes[layer.id];
    if (activeDrag?.layerId === layer.id) return activeDrag.box;
    if (layer.isBackground) {
      return {
        x: 0,
        y: 0,
        width: displaySize.width,
        height: displaySize.height,
      };
    }
    return layerToPixelBox(layer);
  };
  const selectionChrome = useMemo(() => {
    if (!displaySize.width || !displaySize.height) return [];
    const selectedLayers = layers.filter(
      (layer) => selectedSet.includes(layer.id) && isLayerVisible(layer)
    );
    if (!selectedLayers.length) return [];
    const transformableSelected = selectedLayers.filter(
      (layer) => !layer.isBackground && !layer.locked
    );
    const isGroupTransform = transformableSelected.length > 1;
    const layerChrome = selectedLayers.map((layer) => {
      const box = layoutBoxToVisual({
        box: livePixelBoxForLayer(layer),
        zoom,
      });
      const isPrimary = livePrimaryID === layer.id;
      return {
        id: layer.id,
        x: box.x,
        y: box.y,
        width: box.width,
        height: box.height,
        rotation: Number(box.rotation) || 0,
        color: SELECTION_COLOR,
        name: layer.name || layer.id,
        showLabel: isPrimary && !isGroupTransform,
        showHandles:
          isPrimary &&
          !isGroupTransform &&
          !isMarqueeSelecting &&
          !layer.isBackground &&
          !layer.locked &&
          !isObjectEraserActive &&
          editingTextID !== layer.id,
        cropHandles:
          (activeDrag?.mode === "crop" || (!activeDrag && shiftHeld)) &&
          canCropImageLayer({
            type: getLayerType(layer),
            isBackground: layer.isBackground,
          }),
      };
    });
    const groupChrome = isGroupTransform
      ? groupSelectionChrome({
          bounds: axisAlignedBounds(
            transformableSelected.map((layer) =>
              layoutBoxToVisual({
                box: livePixelBoxForLayer(layer),
                zoom,
              })
            )
          ),
          showHandles: !isObjectEraserActive && !isMarqueeSelecting,
        })
      : null;
    return groupChrome ? [...layerChrome, groupChrome] : layerChrome;
    // eslint-disable-next-line
  }, [
    activeDrag,
    displaySize.height,
    displaySize.width,
    editingTextID,
    isMarqueeSelecting,
    isObjectEraserActive,
    layers,
    livePrimaryID,
    selectedSet,
    shiftHeld,
    zoom,
  ]);

  const onArtboardDoubleClick = (event) => {
    if (isObjectEraserActive) return;
    const canvas = artboardRef.current;
    if (!canvas || !displaySize.width || !displaySize.height) return;
    const point = clientToCanvasPixels(event, canvas);
    const hitLayer = hitTestEditableLayer({
      layers,
      point,
      displayWidth: displaySize.width,
      displayHeight: displaySize.height,
      preferText: true,
    });
    if (
      hitLayer &&
      getLayerType(hitLayer) === LAYER_TYPE.TEXT &&
      !hitLayer.locked
    ) {
      onSelectLayer(hitLayer.id, event);
      onStartTextEdit(hitLayer.id);
    }
  };

  const onEraseObjectClick = async () => {
    if (!eraserLayer || erasingLayerIds[eraserLayer.id]) return;
    const mask = await eraserOverlayRef.current?.exportMask?.();
    if (!mask?.blob) return;
    await onEraseObject(
      buildEraseLayerObjectRequest({
        layer: eraserLayer,
        maskBlob: mask.blob,
        maskWidth: mask.width,
        maskHeight: mask.height,
        canvasWidth,
        canvasHeight,
      })
    );
  };

  return (
    <Box
      data-image-layers-canvas
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        height: "100%",
        maxHeight: "100%",
      }}
    >
      <Box
        sx={{
          flex: 1,
          px: { xs: 2, sm: 4 },
          pt: { xs: 1.5, sm: 1.5 },
          pb: { xs: 1, sm: 2 },
          overflow: "hidden",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          ...canvasWorkspaceSx,
        }}
      >
        {!isSidebarOpen || toolbar || showCloseButton ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              flexShrink: 0,
              minHeight: 40,
              mb: 1,
              zIndex: 10,
            }}
          >
            {!isSidebarOpen ? (
              <IconButton
                onClick={onToggleSidebar}
                sx={{
                  position: "absolute",
                  left: -8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 10,
                }}
              >
                <MenuIcon />
              </IconButton>
            ) : null}

            {toolbar}

            {showCloseButton ? (
              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  right: -8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 10,
                }}
              >
                <CloseIcon />
              </IconButton>
            ) : null}
          </Box>
        ) : null}

        {imageUrl ? (
          <>
            <Box
              sx={{
                position: "relative",
                flex: 1,
                minHeight: 0,
                width: "100%",
                height: "100%",
              }}
            >
              <Box
                ref={viewportRef}
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  flex: 1,
                  minHeight: 0,
                  maxWidth: "100%",
                  maxHeight: "100%",
                  ...canvasZoomViewportSx,
                  ...(zoom <= 1.001 ? canvasZoomHideScrollbarsSx : null),
                }}
              >
                <Box
                  onPointerDown={onPointerDownWorkspace}
                  onPointerMove={onPointerMoveWorkspace}
                  onPointerUp={onPointerUpWorkspace}
                  onPointerCancel={onPointerUpWorkspace}
                  sx={{
                    position: "relative",
                    width: contentWidth,
                    height: contentHeight,
                    overflow: "visible",
                    touchAction: "none",
                  }}
                >
                  {hasLayers && displaySize.width > 0 ? (
                    <Box
                      ref={artboardRef}
                      onPointerDown={onArtboardPointerDown}
                      onPointerMove={onArtboardPointerMove}
                      onPointerUp={onArtboardPointerUp}
                      onPointerCancel={onArtboardPointerUp}
                      onDoubleClick={onArtboardDoubleClick}
                      sx={{
                        isolation: "isolate",
                        overflow: "visible",
                        ...canvasArtboardSx,
                        ...canvasZoomBoardSx({
                          padX,
                          padY,
                          fitWidth,
                          fitHeight,
                          zoom,
                        }),
                        borderRadius: 2,
                        touchAction: "none",
                      }}
                    >
                      <ImageArtboardLayers
                        layers={layers}
                        selectedSet={selectedSet}
                        editingTextID={editingTextID}
                        selectedTextNodeID={selectedTextNodeID}
                        selectedTextPhrase={selectedTextPhrase}
                        onTextCommit={onTextCommit}
                        onTextSelectionChange={onTextSelectionChange}
                        altFallback={t(MODAL_IMAGE_EDIT_IMAGE_ALT)}
                        scale={scale}
                        canvasWidth={canvasWidth}
                        canvasHeight={canvasHeight}
                        dragBoxes={activeDrag?.boxes}
                        dragLayerId={activeDrag?.layerId}
                        dragBox={activeDrag?.box}
                        displayWidth={displaySize.width}
                        displayHeight={displaySize.height}
                      />
                      {eraserLayer && eraserPixelBox ? (
                        <ObjectEraserOverlay
                          ref={eraserOverlayRef}
                          layer={eraserLayer}
                          pixelBox={eraserPixelBox}
                          canvasWidth={canvasWidth}
                          canvasHeight={canvasHeight}
                          brushFraction={objectEraserBrushFraction}
                          onHasMaskChange={onHasObjectEraserMaskChange}
                        />
                      ) : null}
                      {layers.map((layer) => {
                        if (!erasingLayerIds[layer.id] || !displaySize.width) {
                          return null;
                        }
                        const box = layer.isBackground
                          ? {
                              x: 0,
                              y: 0,
                              width: displaySize.width,
                              height: displaySize.height,
                            }
                          : activeDrag?.layerId === layer.id
                            ? activeDrag.box
                            : normalizedToPixels({
                                x: layer.x,
                                y: layer.y,
                                width: layer.width,
                                height: layer.height,
                                rotation: layer.rotation,
                                displayWidth: displaySize.width,
                                displayHeight: displaySize.height,
                              });
                        return (
                          <LayerEraseProgressOverlay
                            key={`erase-progress-${layer.id}`}
                            box={box}
                          />
                        );
                      })}
                      <ScannerOverlay visible={isAnalyzing} />
                    </Box>
                  ) : displaySize.width > 0 ? (
                    <Box
                      ref={artboardRef}
                      sx={{
                        ...canvasZoomBoardSx({
                          padX,
                          padY,
                          fitWidth,
                          fitHeight,
                          zoom,
                        }),
                      }}
                    >
                      <Box
                        component="img"
                        src={imageUrl}
                        alt={t(MODAL_IMAGE_EDIT_IMAGE_ALT)}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          borderRadius: 2,
                          boxShadow: 3,
                          border: "1px solid rgba(0, 0, 0, 0.12)",
                          display: "block",
                        }}
                      />
                      <ScannerOverlay visible={isAnalyzing} />
                      {isAnalyzing ? (
                        <Typography
                          variant="body2"
                          sx={{
                            position: "absolute",
                            left: "50%",
                            bottom: 16,
                            transform: "translateX(-50%)",
                            bgcolor: "rgba(255,255,255,0.9)",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontWeight: 600,
                            zIndex: 4,
                          }}
                        >
                          {t(MODAL_IMAGE_EDIT_LAYERS_ANALYZING)}
                        </Typography>
                      ) : null}
                    </Box>
                  ) : null}
                  {marquee ? (
                    <Box
                      sx={marqueeOverlaySx({
                        x: padX + marquee.x,
                        y: padY + marquee.y,
                        w: marquee.w,
                        h: marquee.h,
                      })}
                    />
                  ) : null}
                </Box>
              </Box>
              <SelectionChromeOverlay
                boardRef={artboardRef}
                viewportRef={viewportRef}
                selections={selectionChrome}
                syncKey={`${zoom}-${displaySize.width}-${displaySize.height}-${selectedSet.join(",")}`}
                onHandlePointerDown={onOverlayHandlePointerDown}
              />
              <AlignmentGuidesOverlay
                ref={alignmentGuidesRef}
                boardRef={artboardRef}
                viewportRef={viewportRef}
                syncKey={`${zoom}-${displaySize.width}-${displaySize.height}`}
              />
              <RotationAngleOverlay ref={rotationAngleRef} />
            </Box>
            <ZoomControls
              zoom={zoom}
              onZoomIn={onZoomIn}
              onZoomOut={onZoomOut}
              onFit={onFit}
              leading={
                <AlignmentGuidesToggle
                  enabled={alignmentGuidesEnabled}
                  onToggle={() => setAlignmentGuidesEnabled((value) => !value)}
                />
              }
            />
          </>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
              {t(MODAL_IMAGE_EDIT_IMAGE_NOT_FOUND)}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 400 }}
            >
              {t(MODAL_IMAGE_EDIT_START_CREATING)}
            </Typography>
          </Box>
        )}
      </Box>

      {isObjectEraserActive ? (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            alignItems: "center",
            p: 4,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            flexShrink: 0,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={onEraseObjectClick}
            disabled={
              !hasObjectEraserMask || Boolean(erasingLayerIds[eraserLayer?.id])
            }
            loading={Boolean(erasingLayerIds[eraserLayer?.id])}
            sx={{ minWidth: 180 }}
          >
            {t(CANVAS_TOOL_ERASER_APPLY)}
          </Button>
        </Box>
      ) : showSaveActions ? (
        <ImageActions
          imageUrl={imageUrl}
          initialImageUrl={initialImageUrl}
          onCallbackSave={onCallbackSave}
          onClose={onClose}
          showSaveAlert={showSaveAlert}
          disabled={saveDisabled}
        />
      ) : null}
    </Box>
  );
};

export default LayerCanvas;
